import clsx from 'clsx'
import { useState, useMemo, useEffect } from 'react'

import { Flex } from 'components/common/flex/Flex'
import { InfoIcon } from 'components/common/infoIcon/InfoIcon'
import { ReactComponent as DocumentIcon } from 'components/documentsContainer/documents/assets/document-icon.svg'
import { Document } from 'components/documentsContainer/documents/document/Document'
import styles from 'components/documentsContainer/documents/Documents.module.scss'
import { DocumentDto } from 'types/documents'

interface Props {
  documents: DocumentDto[]
  roleFilter: string
  phase?: DocumentDto['phase']
}

export const Documents = ({ documents, roleFilter, phase }: Props) => {
  const navigation = useMemo(() => {
    return {
      tabs: [...new Set(documents.map(document => document.phase))],
      initialTab: documents.find(doc => doc.phase.toLowerCase() === phase?.toLowerCase())?.phase || documents[0].phase,
    }
  }, [documents, phase])

  const [activeNavigationTab, setActiveNavigationTab] = useState(navigation.initialTab)

  const filteredDocuments = useMemo(() => {
    let filteredDocuments = documents.filter(document => document.phase === activeNavigationTab)
    if (roleFilter !== 'All Roles') {
      filteredDocuments = filteredDocuments.filter(
        document => document.roles.includes(roleFilter) || document.roles.includes('All Roles'),
      )
    }
    return filteredDocuments
  }, [documents, activeNavigationTab, roleFilter])

  useEffect(() => {
    setActiveNavigationTab(navigation.initialTab)
  }, [documents, navigation.initialTab])

  const inlineMessage = `Currently viewing Tips & Tricks for <strong>${roleFilter}</strong>`

  return (
    <div className={styles.container}>
      <div className={styles.inlineMessageContainer}>
        {roleFilter !== 'All Roles' && (
          <Flex gap={4} direction="row" align="center" className={styles.inlineMessage}>
            <InfoIcon className={styles.inlineMessageIcon} color="#000000" />
            <span className={styles.inlineMessageText}>
              <div dangerouslySetInnerHTML={{ __html: inlineMessage }} />
            </span>
          </Flex>
        )}
      </div>
      <Flex gap={88} align="start">
        <div className={styles.tabs}>
          {navigation.tabs.map((tab, index) => (
            <div onClick={() => setActiveNavigationTab(tab)} key={index}>
              <Flex
                gap={10}
                align="center"
                className={clsx(styles.tab, activeNavigationTab === tab && styles.activeTab)}
              >
                <DocumentIcon className={styles.documentIcon} />
                <span className={styles.tabText}>
                  {tab}
                </span>
              </Flex>
            </div>
          ))}
        </div>
        <div style={{ width: '100%' }}>
            <div className={styles.title}>
              {activeNavigationTab}
            </div>
          <Flex gap={28} wrap="wrap">
            {filteredDocuments.map((document, index) => (
              <Document document={document} key={index} />
            ))}
            {filteredDocuments.length === 0 && (
              <div className={styles.noDocumentsMessage}>
                No documents found for the selected role
              </div>
            )}
          </Flex>
        </div>
      </Flex>
    </div>
  )
}
